import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import type { Msg } from "./types";
import type { TrainItem, CarItem, FoodItem, GuideItem, FlightItem, TransferPlanItem, HotelSearchItem } from "@/api/aiChat";
import { getTime } from "./engine";
import ChatBubble from "./components/ChatBubble";
import WaveViewCanvas from "./components/WaveViewCanvas";
import { useVoiceStore } from "@/store/useVoiceStore";
import { useChatSessionStore } from "@/store/useChatSessionStore";
import { unlockSharedAudio, installPageTouchListener, playTtsViaWebAudio } from "@/utils/audioPlayer";
import { useUserStore } from "@/store/useUserStore";
import { sendAIChatMessage, fetchAIChatMessages, resolveAudioUrl } from "@/api/aiChat";
import { fetchUserProfile, type UserProfile } from "@/api/user";
import { isAiSystemMsg, buildQueryData, isGreetingMessage } from "./message-utils";

// ─── 主页面组件 ──────────────────────────────────────────
export default function AiVoicePage() {
  const navigate = useNavigate();
  const [isTextMode, setIsTextMode] = useState(false);
  const [textInput, setTextInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // Refs
  const endRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  // ── 从全局 store 获取 VAD 状态和操作 ──
  const {
    vadState,
    isListening,
    isPressRecording,
    deviceStatus,
    waveData,
    audioSilent,
    startPressToRecord,
    stopPressToRecord,
    isAudioProcessing,
    conversationMessages: msgs,
    prependConversationMessages,
    appendConversationMessages,
    updateConversationMessage,
    setVoicePersistEnabled,
    setPendingTypingMsgId,
    isTtsPlaying,
    micError,
    setMicError,
  } = useVoiceStore();

  // 按住录音状态
  const [isHolding, setIsHolding] = useState(false);
  const holdStartRef = useRef<number>(0);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 滚动到底部（历史消息前插时跳过，避免页面跳动）
  const isPrependingHistoryRef = useRef(false);
  const forceScrollRef = useRef(false); // ★ 用户主动发消息时强制滚底

  /** 判断用户是否在聊天容器底部附近（距底部 <= 150px 视为"在看最新消息"） */
  const isUserNearBottom = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el) return true; // 容器不存在时默认允许滚动
    return el.scrollHeight - el.scrollTop - el.clientHeight <= 150;
  }, []);

  useEffect(() => {
    if (isPrependingHistoryRef.current) {
      isPrependingHistoryRef.current = false;
      return;
    }
    // ★ forceScroll=true 时（用户刚发了消息）无视位置判断，强制滚底
    if (forceScrollRef.current) {
      forceScrollRef.current = false;
      endRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    // ★ 用户正在向上查看历史消息时不自动滚底，否则 smooth 滚到最新
    if (isUserNearBottom()) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [msgs, isUserNearBottom]);

  // ★ 页面挂载时安装首次触摸监听，提前解锁音频子系统
  //    确保 SSE 推送 TTS 后的自动播放在所有移动平台都能正常工作
  useEffect(() => {
    installPageTouchListener();
    // ★ 进入页面立即尝试解锁音频（仅对已解锁过的浏览器有效；首次仍需用户手势）
    unlockSharedAudio();
  }, []);

  // ★ 进入页面时自动播报最新 AI 消息的 TTS 语音（如 AI 打招呼）
  //   兼容两种场景：
  //   ① 进入页面前历史消息已带 url（如 SSE 在其他页面时已绑定过 TTS）
  //   ② 进入页面时历史接口才返回 url（异步），需等 url 出现后播放
  //   实时 SSE 新推来的 TTS 仍由 ChatBubble 的 ttsAutoPlay 负责
  const pageAutoPlayedRef = useRef(false);
  // ★ 进入页面时"已存在"的 AI 消息 id 快照：这些属于历史批次，由本页负责自动播放
  const initialMsgIdsRef = useRef<Set<number> | null>(null);

  useEffect(() => {
    if (pageAutoPlayedRef.current) return;
    if (msgs.length === 0) return;

    // 首次有消息时记录快照：进入页面那一刻已存在的消息 id
    if (initialMsgIdsRef.current === null) {
      initialMsgIdsRef.current = new Set(msgs.map((m) => m.id));
    }

    // 从后往前找第一条有 TTS url 的历史 AI 消息（非 typing）
    // 历史接口是异步返回的，url 可能出现得比消息晚，因此每次 msgs 变化都会重扫
    let latestTtsUrl = "";
    let latestMsgId = -1;
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i];
      if (m.role !== "ai" || !m.url || m.isTyping) continue;
      // 仅处理进入页面时就已存在的消息（实时 SSE 的交给 ChatBubble 处理，避免重复播放）
      if (!initialMsgIdsRef.current.has(m.id)) continue;
      latestTtsUrl = m.url;
      latestMsgId = m.id;
      break;
    }

    if (!latestTtsUrl) return;

    pageAutoPlayedRef.current = true;
    const timer = setTimeout(() => {
      console.log("[AiVoicePage] ★ 页面进入 → 自动播报最新 AI 消息语音, msgId=" + latestMsgId + " url=" + latestTtsUrl.substring(0, 60) + "...");
      playTtsViaWebAudio(latestTtsUrl, {
        onPlay: () => {
          console.log("[AiVoicePage] 页面自动播报 → onPlay");
          useVoiceStore.getState().setIsTtsPlaying(true);
        },
        onEnded: () => {
          console.log("[AiVoicePage] 页面自动播报 → onEnded");
          useVoiceStore.getState().setIsTtsPlaying(false);
        },
        onError: () => {
          console.warn("[AiVoicePage] 页面自动播报 → onError");
          useVoiceStore.getState().setIsTtsPlaying(false);
        },
      });
    }, 800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgs]);

  useEffect(() => {
    if (isTextMode) setTimeout(() => textInputRef.current?.focus(), 100);
  }, [isTextMode]);

  // ── 从 ChatSessionStore 获取 session_id ──
  const session_id = useChatSessionStore((state) => state.session_id);

  // ── 从 UserStore 获取登录状态与用户信息（避免对象选择器导致无限循环）──
  const isLoggedIn = useUserStore((s) => s.isLoggedIn);
  const userInfo = useUserStore((s) => s.userInfo);
  const wechatUser = useUserStore((s) => s.wechatUser);
  const token = useUserStore((s) => s.token);
  const isTokenExpired = useUserStore((s) => s.isTokenExpired);

  // ── 从 API 加载用户详细信息获取头像 ──
  const [profile, setProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (!token || isTokenExpired()) return;
    fetchUserProfile()
      .then((data) => setProfile(data.user))
      .catch(() => {});
  }, [token, isTokenExpired]);

  // 四级回退：profile.avatar → wechatUser.avatar → userInfo.avatar → 空字符串
  const userAvatar = profile?.avatar || wechatUser?.avatar || userInfo?.avatar || "";

  // ── ★ 历史记录加载：token 就绪后直接调用 /sse/history（Chat-Token 即可标识用户）──
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const historyLoadedRef = useRef(false);
  const beforeCursorRef = useRef<number | string>(Math.floor(Date.now()));

  useEffect(() => {
    let attempts = 0;
    const MAX_ATTEMPTS = 50; // 50 × 200ms = 10s

    const timer = setInterval(async () => {
      attempts++;

      // 方式1：store 中已有 session_id → 直接加载
      let sid = useChatSessionStore.getState().session_id;
      if (sid && !historyLoadedRef.current) {
        historyLoadedRef.current = true;
        clearInterval(timer);
        loadHistoryData(true);
        return;
      }

      // 方式2：用户已登录（有 token）但 session_id 为空 → 直接用 token 加载历史
      if (!sid && !historyLoadedRef.current) {
        const { token: t } = useUserStore.getState();
        // 也检查 localStorage 兜底
        let tokenVal = t;
        if (!tokenVal) {
          try {
            const raw = localStorage.getItem("user-storage");
            if (raw) {
              const parsed = JSON.parse(raw);
              tokenVal = parsed?.state?.token ?? parsed?.token ?? null;
            }
          } catch {}
        }
        if (tokenVal && attempts >= 5) {
          // 等 1s 让 SSE 连接先建立（确保服务端 session 已创建）
          historyLoadedRef.current = true;
          clearInterval(timer);
          loadHistoryData(true);
          return;
        }
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(timer);
      }
    }, 200);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * 加载历史消息
   *
   * ★ 不需要显式传 session_id：认证由 axios 拦截器注入的 Header
   *   （Chat-Token / Chat-Session）完成，登录态下后端也据此识别用户。
   *
   * @param isInitial true=首次加载（游标取当前时间），false=上拉翻页（用上一页 last_time）
   */
  const loadHistoryData = useCallback(async (isInitial = false) => {
    try {
      const beforeVal = isInitial ? Math.floor(Date.now()) : beforeCursorRef.current;
      if (typeof beforeCursorRef.current !== "string" || isInitial) {
        beforeCursorRef.current = beforeVal;
      }

      const res = await fetchAIChatMessages({ limit: 20, before: beforeVal });

      if (res.messages && res.messages.length > 0) {
        // ★ 查询类型判定 / queryData 规范化 / 系统消息判定 统一收敛到 message-utils
        //    （与 App.tsx handleSSEMessage 完全一致，避免刷新后卡片丢失）

        const msgs = res.messages.map((m: any) => {
          // ★ 角色判断优先用 sender.uid（uid=0=AI, uid>0=用户），fallback 用 id 前缀
          const role = isAiSystemMsg(m)
            || (m.sender?.uid === 0 || String(m.id || "").startsWith("ai_"))
              ? "ai"
              : "user";
          // ★ 系统消息（navigation/tts_audio）优先 content；其余优先 display_content
          const text = isAiSystemMsg(m)
            ? (m.content || "")
            : (m.display_content || m.content || m.message || "");
          // ★ queryData 规范化（含订单 __data）
          const queryData = buildQueryData(m);

          return {
            role,
            text,
            time: m.timestamp
              ? new Date(Number(m.timestamp)).toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })
              : new Date().toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" }),
            url: resolveAudioUrl(m.audio_url ?? m.data?.audio_url),
            // ★ 始终携带 msg_type，方便过滤和渲染判断
            ...(m.msg_type ? { msg_type: m.msg_type } : {}),
            // ★ 查询/订单消息额外携带 queryData，让 ChatBubble 渲染卡片组件
            ...(queryData ? { queryData } : {}),
          };
        }).filter((m: any) => {
          // ★ 查询卡片消息必须保留（历史里的卡片依赖它）
          if (m.queryData) return true;
          // 过滤空消息和 AI 欢迎语（统一规则见 message-utils.isGreetingMessage）
          if (!m.text) return false;
          if (isGreetingMessage(m)) return false;
          return true;
        });

        // ★ 接口返回的是从新到旧，chat 需要从旧到新显示，所以 reverse
        msgs.reverse();

        // ★ 仅"上拉加载更多"时跳过自动滚动，首次加载后仍需滚到底部显示最新对话
        if (!isInitial) isPrependingHistoryRef.current = true;
        prependConversationMessages(msgs as any);

        // ★ 首次历史加载完成后，立即 auto 跳到底部（不等 useEffect 的 smooth）
        if (isInitial) {
          requestAnimationFrame(() => {
            endRef.current?.scrollIntoView({ behavior: "auto" });
          });
        }
        // ★ 用 last_time 作为游标加载下一页（接口用时间戳分页）
        if (res.last_time) beforeCursorRef.current = res.last_time;
        setHasMore(res.has_next && res.messages.length === 20);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("[AiVoicePage] 加载历史失败:", err);
      setHasMore(false);
    }
  }, [prependConversationMessages]);

  /** 上拉加载更多 */
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    try {
      // ★ 认证由 axios 拦截器注入的 Chat-Token / Chat-Session 完成，
      //   登录态 session_id 为空也能正常翻页
      await loadHistoryData(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, loadHistoryData]);

  // ── 滚动到顶部时加载更多 ──
  const handleScroll = useCallback(() => {
    const el = chatContainerRef.current;
    if (!el || isLoadingMore || !hasMore) return;
    if (el.scrollTop < 80) handleLoadMore();
  }, [isLoadingMore, hasMore, handleLoadMore]);

  // 发送消息（文字/语音）
  // 新版 /sse/send 接口只返回 { success, message_id }，不同步返回 AI 内容
  // AI 回复、路由跳转等全部通过 SSE 长连接异步推送（App.tsx handleSSEMessage 处理）
  const send = useCallback(
    async (
      text: string,
      isVoice = false,
      url?: string,
      blob?: Blob,
    ): Promise<void> => {
      const recognizedText = text.trim();
      if (!recognizedText) return Promise.resolve();

      // ★ 利用用户手势解锁移动端音频自动播放
      unlockSharedAudio();

      setIsProcessing(true);

      const uMsg: Omit<Msg, "id"> = {
        role: "user",
        text: recognizedText,
        time: getTime(),
        isVoice,
        url,
      };
      // AI 占位消息，等待 SSE 推送实际回复后替换
      const tMsg: Omit<Msg, "id"> = {
        role: "ai",
        text: "",
        isTyping: true,
        time: getTime(),
      };

      const [, typingMsg] = appendConversationMessages([uMsg, tMsg]);
      forceScrollRef.current = true; // ★ 用户主动发消息，强制滚底
      setPendingTypingMsgId(typingMsg.id);
      setTextInput("");

      try {
        const session_id = useChatSessionStore.getState().session_id;

        let content: string | File = recognizedText;

        // 语音消息：直接将 Blob 转为 File 上传
        if (isVoice && blob) {
          const fileName = `audio_${Date.now()}.webm`;
          content = new File([blob], fileName, {
            type: blob.type || "audio/webm",
          });
        }

        // 发送消息（仅确认已接收，AI 回复由 SSE 推送）
        await sendAIChatMessage({
          type: isVoice ? "audio" : "text",
          content,
          session_id: session_id || undefined,
        });

        // 发送成功，保持 isTyping 状态等待 SSE 推送
      } catch (error) {
        console.error("发送消息失败:", error);
        updateConversationMessage(typingMsg.id, {
          isTyping: false,
          text: "抱歉，消息发送失败，请稍后重试。",
        });
        setPendingTypingMsgId(null);
      }
      // ★ 无论成功或失败都要重置 isProcessing，避免按钮永久 disabled
      setIsProcessing(false);
    },
    [
      appendConversationMessages,
      updateConversationMessage,
      setPendingTypingMsgId,
    ],
  );

  const sendText = useCallback(() => {
    const t = textInput.trim();
    if (!t) return;
    send(t, false);
  }, [textInput, send]);

  const handleNav = (path: string) => navigate(path);

  /**
   * 返回上一页：
   *   - 有历史记录 → navigate(-1) 回到来源页
   *   - 无历史记录（直接打开/刷新/微信分享进入）→ 回首页兜底
   */
  const handleBack = useCallback(() => {
    const canGoBack =
      typeof window !== "undefined" &&
      window.history.length > 1 &&
      (window.history.state?.idx ?? 0) > 0;
    if (canGoBack) {
      navigate(-1);
    } else {
      navigate("/home", { replace: true });
    }
  }, [navigate]);


  // ── 点击车次行 → 发送预定请求给后端 ──
  const handleTrainSelect = useCallback(
    async (train: TrainItem) => {
      if (!train.can_buy) return;
      setIsProcessing(true);

      const bookText = `我要预定 ${train.train_no} 次列车（${train.departure}→${train.destination}，${train.departure_time}发车）`;
      
      // 显示用户消息
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底

      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预定请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击租车行 → 发送预定请求 ──
  const handleCarSelect = useCallback(
    async (car: CarItem) => {
      setIsProcessing(true);
      const bookText = `我要预定 ${car.brand} ${car.model}${car.scenario ? `（${car.scenario}）` : ""}`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预定请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击餐厅行 → 发送预订请求 ──
  const handleFoodSelect = useCallback(
    async (food: FoodItem) => {
      setIsProcessing(true);
      const bookText = `我想预订 ${food.name}${food.address ? `（${food.address}）` : ""}`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预订请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击导游行 → 发送预约请求 ──
  const handleGuideSelect = useCallback(
    async (guide: GuideItem) => {
      setIsProcessing(true);
      const bookText = `我想预约导游 ${guide.name}${guide.place ? `（${guide.place}）` : ""}`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预约请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击航班行 → 发送订票请求 ──
  const handleFlightSelect = useCallback(
    async (flight: FlightItem) => {
      setIsProcessing(true);
      const bookText = `我要预定 ${flight.name}（${flight.sarttime}-${flight.endtime}，${flight.startaddr}→${flight.endaddr}）`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "订票请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击酒店行 → 发送预订请求 ──
  const handleHotelSelect = useCallback(
    async (hotel: HotelSearchItem) => {
      setIsProcessing(true);
      const bookText = `我想预订 ${hotel.name}${hotel.address ? `（${hotel.address}）` : ""}`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预订请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── 点击接送方案 → 发送预定请求 ──
  const handleTransferSelect = useCallback(
    async (plan: TransferPlanItem) => {
      setIsProcessing(true);
      const bookText = `帮我预定 ${plan.plan_name}（${plan.vehicle_model}${plan.capacity ? `，${plan.capacity}座` : ""}）`;
      appendConversationMessages([
        { role: "user", text: bookText, time: getTime() },
        { role: "ai", text: "", isTyping: true, time: getTime() },
      ]);
      forceScrollRef.current = true; // ★ 用户主动操作，强制滚底
      try {
        await sendAIChatMessage({
          type: "text",
          content: bookText,
          session_id: useChatSessionStore.getState().session_id || undefined,
        });
      } catch {
        updateConversationMessage(
          useVoiceStore.getState().conversationMessages[useVoiceStore.getState().conversationMessages.length - 1]?.id ?? 0,
          { isTyping: false, text: "预定请求发送失败，请稍后重试。" }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [appendConversationMessages],
  );

  // ── ★ 按住录音（微信式交互）──

  /** 按下/触摸开始 */
  const handlePressStart = useCallback(async () => {
    if (isProcessing || deviceStatus !== "available" || isAudioProcessing) return;
    setIsHolding(true);
    holdStartRef.current = Date.now();

    // 短暂延迟后开始录音，防止误触
    holdTimerRef.current = setTimeout(async () => {
      const ok = await startPressToRecord();
      if (!ok) setIsHolding(false);
    }, 150);
  }, [isProcessing, deviceStatus, isAudioProcessing, startPressToRecord]);

  /** 松手/触摸结束 → 发送 */
  const handlePressEnd = useCallback(async (cancelled = false) => {
    if (!isHolding) return;

    // 清除未开始的定时器（按得太快）
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    // 按住时间太短视为取消
    const heldMs = Date.now() - holdStartRef.current;
    if (heldMs < 300 && !cancelled) cancelled = true;

    setIsHolding(false);

    // 停止录音并输出 Blob
    await stopPressToRecord(cancelled);
  }, [isHolding, stopPressToRecord]);

  /** 触摸滑动到按钮外 → 取消 */
  const handlePressCancel = useCallback(() => {
    handlePressEnd(true);
  }, [handlePressEnd]);

  // VAD 状态提示
  const deviceStatusLabel: Record<string, string> = {
    checking: "正在检测设备...",
    available: "",
    unavailable: "未检测到麦克风",
    denied: "麦克风权限已被拒绝",
  };

  const vadStateLabel: Record<string, string> = {
    idle:
      deviceStatus === "checking"
        ? "正在检测设备..."
        : deviceStatus === "unavailable"
          ? "未检测到麦克风"
          : deviceStatus === "denied"
            ? "麦克风权限被拒绝，请在浏览器设置中允许"
            : "按住说话",
    recording: "正在聆听...",
    speaking: "正在录入",
    silence_detecting: "正在录入",
    press_recording: "松开发送",
  };

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden bg-[#F5F6F8]"
      style={{
        fontFamily:
          "'SF Pro Text', 'PingFang SC', 'Inter', system-ui, sans-serif",
      }}
    >
      {/* ── 顶部导航栏 ── */}
      <div
        className="flex-shrink-0 relative z-10 px-4 pt-3 pb-2 flex items-center justify-between safe-area-top"
        style={{ backgroundColor: "#fff", borderBottom: "1px solid #F0F0F0", paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
      >
        {/* 返回按钮 */}
        <button
          onClick={handleBack}
          aria-label="返回"
          className="w-9 h-9 -ml-1 flex items-center justify-center rounded-full cursor-pointer active:scale-95 transition-all"
          style={{ backgroundColor: "#F5F6F8" }}
        >
          <i className="ri-arrow-left-s-line text-[#333]" style={{ fontSize: 22 }} />
        </button>
        <span className="text-[16px] font-bold text-[#333] tracking-tight">
          Mex Claw
        </span>
        <button
          onClick={() => navigate("/profile")}
          className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-95 transition-all overflow-hidden"
          style={{ backgroundColor: "#F5F6F8" }}
        >
          {isLoggedIn && userAvatar ? (
            <img src={userAvatar} alt="" className="w-full h-full object-cover" />
          ) : (
            <i className="ri-user-line text-[17px] text-[#666]" />
          )}
        </button>
      </div>

      {/* ── 欢迎区域 ── */}
      <div
        className="flex-shrink-0 relative z-10 px-5 pt-3 pb-3"
        style={{ backgroundColor: "#F5F6F8" }}
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-0.5 pt-2">
            <span className="text-[15px] font-medium" style={{ color: "#00A043" }}>哈喽</span>
            <span className="text-[22px] font-bold leading-tight" style={{ color: "#00C851" }}>
              美伊行旅行管家
            </span>
            <div className="flex items-center gap-1 mt-0.5">
              <i
                className="ri-sparkling-fill"
                style={{ color: "#00C851", fontSize: 14 }}
              />
              <span className="text-[12px] text-[#999]">你的旅行需求，一站解决</span>
            </div>
          </div>
          <img
            src="/image.png"
            alt=""
            className="w-28 h-24 flex-shrink-0 object-contain"
            style={{ objectPosition: "right center" }}
          />
        </div>
      </div>

      {/* ── 对话区 ── */}
      <div
        ref={chatContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 relative z-10 bg-[#F5F6F8]"
        style={{ paddingBottom: `calc(${isTextMode ? 110 : 220}px + env(safe-area-inset-bottom, 0px))` }}
      >
        {msgs.map((m) => (
          <ChatBubble
            key={m.id}
            msg={m}
            onNav={handleNav}
            onTrainSelect={handleTrainSelect}
            onCarSelect={handleCarSelect}
            onFoodSelect={handleFoodSelect}
            onGuideSelect={handleGuideSelect}
            onFlightSelect={handleFlightSelect}
            onTransferSelect={handleTransferSelect}
            onHotelSelect={handleHotelSelect}
          />
        ))}
        {/* 上拉加载更多指示 */}
        {isLoadingMore && (
          <div className="flex items-center justify-center py-3">
            <span className="text-xs text-[#999]">加载历史消息中...</span>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* ── 麦克风错误提示横幅 ── */}
      {micError && (
        <div
          className="flex-shrink-0 relative z-10 mx-4 mt-2 px-4 py-2.5 rounded-xl flex items-center justify-between"
          style={{
            background: "#FFF2F0",
            border: "1px solid #FECACA",
          }}
        >
          <div className="flex items-center gap-2.5">
            <i
              className="ri-error-warning-line flex-shrink-0"
              style={{ color: "#EF4444", fontSize: 16 }}
            />
            <span
              className="text-xs font-medium"
              style={{ color: "#DC2626" }}
            >
              {micError.message}
            </span>
          </div>
          <button
            onClick={() => setMicError(null)}
            className="flex-shrink-0 cursor-pointer ml-3"
          >
            <i
              className="ri-close-line"
              style={{ color: "#999", fontSize: 14 }}
            />
          </button>
        </div>
      )}

      {/* ── 底部操作区 ── */}
      <div
        className="flex-shrink-0 relative z-10"
        style={{
          background: "#fff",
          borderTop: "1px solid #F0F0F0",
        }}
      >
        {isTextMode ? (
          /* ─── 文字模式 ─── */
          <div className="px-4 pt-3 pb-8 space-y-2.5" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 flex items-center gap-2 px-4 rounded-2xl"
                style={{
                  background: "#F5F6F8",
                  border: "1px solid #E5E7EB",
                  height: 50,
                }}
              >
                <i
                  className="ri-chat-voice-line flex-shrink-0"
                  style={{ color: "#999", fontSize: 15 }}
                />
                <input
                  ref={textInputRef}
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendText();
                  }}
                  placeholder="输入想说的话..."
                  className="flex-1 bg-transparent outline-none"
                  style={{ color: "#333", fontSize: 14 }}
                />
                {textInput && (
                  <button
                    onClick={() => setTextInput("")}
                    className="flex-shrink-0 cursor-pointer"
                  >
                    <i
                      className="ri-close-circle-fill"
                      style={{ color: "#ccc", fontSize: 16 }}
                    />
                  </button>
                )}
              </div>
              <button
                onClick={sendText}
                className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0 cursor-pointer active:scale-95 transition-all"
                style={{
                  background: textInput.trim()
                    ? "linear-gradient(135deg, #00C851, #00A043)"
                    : "#F0F0F0",
                  border: "none",
                }}
              >
                <i
                  className="ri-send-plane-2-fill"
                  style={{
                    color: textInput.trim() ? "#fff" : "#bbb",
                    fontSize: 18,
                  }}
                />
              </button>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setIsTextMode(false);
                  setVoicePersistEnabled(true);
                }}
                className="flex items-center gap-1.5 px-5 py-2 rounded-full cursor-pointer text-xs whitespace-nowrap active:scale-95 transition-all"
                style={{
                  background: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  color: "#00C851",
                }}
              >
                <i className="ri-mic-line" style={{ fontSize: 12 }} />
                返回语音对讲
              </button>
            </div>
          </div>
        ) : (
          /* ─── 语音模式 ─── */
          <div className="pt-3 pb-8" style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}>
            <div className="mx-4 mb-2">
              <WaveViewCanvas
                width={Math.min(340, window.innerWidth - 32)}
                height={44}
                active={isListening && !isProcessing}
                audioSilent={audioSilent}
                isTtsPlaying={isTtsPlaying}
                statusText={
                  isAudioProcessing
                    ? "正在处理..."
                    : deviceStatusLabel[vadState] || vadStateLabel[vadState]
                }
                vadState={vadState}
                lineWidth={2}
                getWaveData={() => waveData}
              />
            </div>

            {/* 底部操作栏 */}
            <div className="flex items-center justify-between gap-2.5 px-4 mt-1">
              {/* ★ 按住说话按钮（微信式交互） */}
              <button
                onMouseDown={handlePressStart}
                onMouseUp={() => handlePressEnd(false)}
                onMouseLeave={handlePressCancel}
                onTouchStart={(e) => { e.preventDefault(); handlePressStart(); }}
                onTouchEnd={(e) => { e.preventDefault(); handlePressEnd(false); }}
                onTouchCancel={handlePressCancel}
                onContextMenu={(e) => e.preventDefault()}
                disabled={
                  isProcessing || isAudioProcessing || (deviceStatus !== "available")
                }
                className={`flex-1 flex items-center justify-center gap-1.5 py-3 rounded-2xl cursor-pointer select-none active:scale-95 transition-all whitespace-nowrap touch-none ${
                  isProcessing || isAudioProcessing || deviceStatus !== "available"
                    ? "opacity-40 cursor-not-allowed"
                    : ""
                }`}
                style={{
                  background: (isHolding || isPressRecording)
                    ? "#FEF2F2"
                    : deviceStatus === "available"
                      ? "#F0FDF4"
                      : "#F5F6F8",
                  border: `1px solid ${
                    (isHolding || isPressRecording)
                      ? "#FECACA"
                      : deviceStatus === "available"
                        ? "#BBF7D0"
                        : "#E5E7EB"                  }`,
                }}
              >
                <i
                  className={                    (isHolding || isPressRecording)
                      ? "ri-stop-circle-fill"
                      : deviceStatus === "available"
                        ? "ri-mic-line"
                        : "ri-mic-off-line"
                  }
                  style={{                    fontSize: 16,                    color: (isHolding || isPressRecording)
                      ? "#EF4444"
                      : deviceStatus === "available"
                        ? "#00C851"
                        : "#bbb",
                  }}                />
                <span                  className="text-xs font-medium"                  style={{                    color: (isHolding || isPressRecording)
                      ? "#EF4444"
                      : deviceStatus === "available"
                        ? "#00C851"
                        : "#999",
                  }}                >                  {(isHolding || isPressRecording)                    ? "松开发送"                    : deviceStatus === "available"
                      ? "按住说话"                      : deviceStatusLabel[vadState] || "不可用"}                </span>
              </button>

              <button
                onClick={() => {
                  setIsTextMode(true);
                  setVoicePersistEnabled(false);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-2xl cursor-pointer active:scale-95 transition-all whitespace-nowrap"
                style={{
                  background: "#F5F6F8",
                  border: "1px solid #E5E7EB",
                }}
              >
                <i
                  className="ri-keyboard-line"
                  style={{ fontSize: 14, color: "#666" }}
                />
                <span
                  className="text-xs"
                  style={{ color: "#666" }}
                >
                  文字输入
                </span>
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes bDot { 0%,80%,100%{transform:translateY(0);opacity:.3} 40%{transform:translateY(-4px);opacity:1} }
        @keyframes statusPulse { 0%,100%{opacity:1} 50%{opacity:0.25} }
      `}</style>
    </div>
  );
}
